// Smart Sash Admin Panel - Institution Store
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  InstitutionDTO,
  CreateInstitutionRequest,
  UpdateInstitutionRequest,
  PaginationMetadata,
} from '@/models';
import { institutionService } from '@/services/api/institutionService';
import { getErrorMessage } from '@/utils/getErrorMessage';

interface InstitutionState {
  institutions: InstitutionDTO[];
  selectedInstitution: InstitutionDTO | null;
  pagination: PaginationMetadata | null;
  isLoading: boolean;
  error: string | null;
  fetchInstitutions: (page?: number, pageSize?: number) => Promise<void>;
  fetchInstitutionById: (id: string) => Promise<void>;
  createInstitution: (data: CreateInstitutionRequest) => Promise<InstitutionDTO>;
  updateInstitution: (id: string, data: UpdateInstitutionRequest) => Promise<void>;
  deleteInstitution: (id: string) => Promise<void>;
  setSelectedInstitution: (institution: InstitutionDTO | null) => void;
  clearError: () => void;
}

export const useInstitutionStore = create<InstitutionState>()(
  devtools(
    (set) => ({
      institutions: [],
      selectedInstitution: null,
      pagination: null,
      isLoading: false,
      error: null,
      fetchInstitutions: async (page = 1, pageSize = 10) => {
        set({ isLoading: true, error: null });
        try {
          const response = await institutionService.getAll({ page, pageSize });
          set({
            institutions: response.data,
            pagination: response.pagination,
            isLoading: false,
          });
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to fetch institutions'),
            isLoading: false,
          });
          throw error;
        }
      },
      fetchInstitutionById: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          const institution = await institutionService.getById(id);
          set({ selectedInstitution: institution, isLoading: false });
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to fetch institution'),
            isLoading: false,
          });
          throw error;
        }
      },
      createInstitution: async (data: CreateInstitutionRequest) => {
        set({ isLoading: true, error: null });
        try {
          const newInstitution = await institutionService.create(data);
          set((state) => ({
            institutions: [...state.institutions, newInstitution],
            isLoading: false,
          }));
          return newInstitution;
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to create institution'),
            isLoading: false,
          });
          throw error;
        }
      },
      updateInstitution: async (id: string, data: UpdateInstitutionRequest) => {
        set({ isLoading: true, error: null });
        try {
          const updatedInstitution = await institutionService.update(id, data);
          set((state) => ({
            institutions: state.institutions.map((i) =>
              i.id === id ? updatedInstitution : i
            ),
            selectedInstitution:
              state.selectedInstitution?.id === id
                ? updatedInstitution
                : state.selectedInstitution,
            isLoading: false,
          }));
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to update institution'),
            isLoading: false,
          });
          throw error;
        }
      },
      deleteInstitution: async (id: string) => {
        set({ isLoading: true, error: null });
        try {
          await institutionService.delete(id);
          set((state) => ({
            institutions: state.institutions.filter((i) => i.id !== id),
            selectedInstitution:
              state.selectedInstitution?.id === id ? null : state.selectedInstitution,
            isLoading: false,
          }));
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to delete institution'),
            isLoading: false,
          });
          throw error;
        }
      },
      setSelectedInstitution: (institution: InstitutionDTO | null) =>
        set({ selectedInstitution: institution }),
      clearError: () => set({ error: null }),
    }),
    { name: 'InstitutionStore' }
  )
);
